import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import {
  CustomPaging,
  EditingState,
  FilteringState,
  IntegratedFiltering,
  IntegratedPaging,
  IntegratedSelection,
  IntegratedSorting,
  PagingState,
  SelectionState,
  SortingState
} from '@devexpress/dx-react-grid';
import {
  ColumnChooser,
  DragDropProvider,
  Grid,
  PagingPanel,
  Table,
  TableColumnReordering,
  TableColumnVisibility,
  TableEditColumn,
  TableEditRow,
  TableFilterRow,
  TableHeaderRow,
  TableSelection,
  Toolbar,
  VirtualTable
} from '@devexpress/dx-react-grid-material-ui';
import React, { forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState, useId } from 'react';
import { TableComponent } from '../NRReactGridCustomComponents/TableComponent/TableComponent';
import { CellComponent, NoDataCellComponent } from '../NRReactGridCustomComponents/CellComponent/CellComponent';
import SortLabel from '../NRReactGridCustomComponents/SortLabel/SortLabel';
import { HeaderCell } from '../NRReactGridCustomComponents/HeaderCell/HeaderCell';
import CopyToClipboardBanner from '../NRReactGridCustomComponents/CopyToClipboardBanner/CopyToClipboardBanner';
import { isEmpty } from '../../utils/utils';
import { FilterCell } from '../NRDeviceInventory/NRDeviceInventoryComponents/FilterCell/FilterCell';
import Avatar from '@mui/material/Avatar';
import ToggleComponent from '../NRReactGridCustomComponents/FilterToggleButtonComponent';
import { Action, Getter } from '@devexpress/dx-react-core';
import useNRMutation from '../../graphql/UseNRMutation';
import { CONSTANTS } from '../../constants/constants';
import { PLACEHOLDER_MUTATION } from '../../graphql/queries/default';
import EditRowCustomCellComponent from '../NRReactGridCustomComponents/TableEdit/EditRowCustomCellComponent';
import { Command } from '../NRReactGridCustomComponents/TableEdit/CommandComponent/CommandComponent';
import HeaderCellComponent from '../NRReactGridCustomComponents/TableEdit/HeaderCellComponent';
import EditColumnCustomCellComponent from '../NRReactGridCustomComponents/TableEdit/EditColumnCustomCellComponent';
import NRColumnSettingsIcons from '../../NRIcons/NRColumnSettingsIcons';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import { SelectAllHeaderCell } from '../NRReactGridCustomComponents/SelectionCell/SelectionCell';
import { CursorPagerComponent } from './plugins/CursorPagination';
import NRFilterListIcon from '../../NRIcons/NRFilterListIcon';
import ToggleButton from '@mui/material/ToggleButton';
import debounce from 'lodash.debounce';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import RowComponent from '../NRReactGridCustomComponents/RowComponent/RowComponent';
import Box from '@mui/material/Box';
import { GridContext } from './NRReactGridContext';
import useLocalStorageService, { LOCAL_STORAGE_CONSTANTS } from '../../services/local';
import useSessionStorageService from '../../services/session';
import { useLocation, useNavigate } from 'react-router-dom';
import { alpha, styled } from '@mui/material/styles';
import { useTheme } from '@mui/styles';
import NRExportToCsvDialog from '../NRExportToCsvDialog/NRExportToCsvDialog';
import NRExportIcon from '../../NRIcons/NRExportIcon';
import isEqual from 'lodash.isequal';
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 200];
const DEFAULT_VARIANT = 'outlined';

const { FILTER_VISIBILITY_VALUE, FIRST, COLUMN_ORDER, HIDDEN_COLUMN_NAMES, SORT } = LOCAL_STORAGE_CONSTANTS.TABLE;

const StyledGridTitle = styled(props => {
  return <Typography data-testid="grid-title" {...props} component="h3" />;
})(({ theme }) => ({
  color: theme.palette.text.secondary,
  ...theme.typography.h3
}));

const StyledAvatar = styled(props => {
  return <Avatar variant="rounded" {...props} />;
})(({ theme }) => ({
  color: theme.palette.getContrastText(theme.palette.primary.main),
  backgroundColor: theme.palette.primary.main,
  width: theme.spacing(5),
  height: theme.spacing(5),
  marginRight: theme.spacing(2)
}));

const StyledTableEditRow = styled(props => {
  return <TableEditRow.Row {...props} />;
})(({ theme }) => ({
  backgroundColor: `${alpha(theme.palette.primary.main, 0.1)} !important`,
  boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.24)'
}));

const editingIdSetter = (rowIds, editingIds, setEditingIds) => {
  setEditingIds(editingIds.filter(e => e !== rowIds[0]));
};

let lastInput = null;
let lastResult = null;
const { INFO, USER_VERIFICATION, LATEST_REMEDIATION } = CONSTANTS.TABLE.COLUMN;
const nonDisableAbleColumns = [INFO, USER_VERIFICATION, LATEST_REMEDIATION];

const FilterRow = React.memo(props => <Table.Row {...props} className="TableFilterRow-row" />);

// Component for locked (non-draggable) column headers
const LockedHeaderCell = React.memo(({ headerCellProps }) => (
  <Box
    component="div"
    onMouseDown={e => {
      // Prevent drag initiation at the earliest point
      e.stopPropagation();
      e.preventDefault();
    }}
    onDragStart={e => {
      e.preventDefault();
      e.stopPropagation();
      return false;
    }}
    onDrag={e => {
      e.preventDefault();
      e.stopPropagation();
      return false;
    }}
    sx={{
      cursor: 'default !important',
      '& *': {
        cursor: 'default !important'
      }
    }}
  >
    <HeaderCell
      {...headerCellProps}
      draggingEnabled={false}
      style={{
        ...headerCellProps.style,
        cursor: 'default',
        userSelect: 'none'
      }}
    />
  </Box>
));

function NRReactGrid(
  {
    rows = [],
    columns,
    columnExtensions,
    integratedSortingColumnExtension,
    pageSizes = PAGE_SIZE_OPTIONS,
    pageSize = 25,
    title,
    titleRightIcon,
    loading = false,
    statesArray,
    providersArray,
    renderersArray,
    selectionCell,
    selectAllHeaderCell,
    selectAllAriaLabel,
    showTitle = true,
    showSelectAll = true,
    showIntegratedSelection = true,
    showIntegratedPagination = true,
    customClass,
    variant = DEFAULT_VARIANT,
    elevation = 1,
    titleExtraComponent,
    preSettedFilters,
    tableIcon,
    showFilterSelector = true,
    isTableEditable = false,
    hasExtraActions = false,
    qlQuery,
    qlVersion = 'v2',
    updateAfterUpdate,
    allowColumnReOrdering = true,
    infoCellProvider,
    defaultSortingColumn,
    defaultSortingOrder = 'desc',
    sortingColumnNames,
    getDataFrom,
    hasCursorPagination = false,
    totalCount,
    handleFetchMore,
    pageInfo,
    onSortingChange,
    handleFiltersChange,
    onChangeFiltersVisibility,
    tableKey,
    fullHeight = false,
    isVirtualTable = false,
    scrollToRowId,
    addDefaultSorting = true,
    externalHiddenColumnNames,
    tableStorageId,
    handleOnPageSizeChange,
    selectAllFetchConfig,
    hideCustomColumns = false,
    hideColumnFiltering = false,
    hideTableHeaderRow = false,
    specialCaseCSV = false
  },
  ref
) {
  const theme = useTheme();
  const titleId = useId();
  const [selection, setSelection] = useState([]);
  const [filters, setFilters] = useState(preSettedFilters || []);
  const [sorting, setSorting] = useState();
  const [editingIds, setEditingIds] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [showResetFilters, setShowResetFilters] = useState(false);
  const [exportToCsvDialogIsOpen, setExportToCsvDialogIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const { shouldClearSelection, setShouldClearSelection } = useContext(GridContext);
  const [sortingColumnExtension] = useState(integratedSortingColumnExtension);

  const vtRef = useRef();

  const { getStorageKeyData, setStorageKeyData } = useLocalStorageService();
  const { clearSessionKeyData, getSessionKeyData } = useSessionStorageService();
  const tableSettingsStoredData = !!tableStorageId && getStorageKeyData(tableStorageId);

  const [filtersVisibilityValue, setFiltersVisibilityValue] = useState(
    tableSettingsStoredData?.[FILTER_VISIBILITY_VALUE] != null ? tableSettingsStoredData?.[FILTER_VISIBILITY_VALUE] : false
  );
  const [tablePageSize, setTablePageSize] = useState(tableSettingsStoredData?.[FIRST] || pageSize);

  useEffect(() => {
    if (!tableStorageId) return;
    if (tableSettingsStoredData?.[FIRST] !== tablePageSize) {
      setStorageKeyData(tableStorageId, { key: FIRST, value: tablePageSize });
    }
  }, [tablePageSize]);

  // Track columns that should not be reorderable (allowReordering: false)
  const lockedCols = useMemo(
    () => columns.map((item, index) => (item.allowReordering === false ? { name: item.name, originalIndex: index } : null)).filter(Boolean),
    [columns]
  );

  // Set for O(1) lookup when checking if a column is locked
  const lockedColNames = useMemo(() => new Set(lockedCols.map(col => col.name)), [lockedCols]);

  // Validate and merge saved column order with current columns
  // This handles cases where columns were added/removed after the order was saved
  const getValidatedColumnOrder = useCallback((savedOrder, currentColumns) => {
    if (!savedOrder || !Array.isArray(savedOrder)) {
      return currentColumns.map(column => column.name);
    }

    const currentColumnNames = new Set(currentColumns.map(col => col.name));

    // Filter saved order to current columns and deduplicate (keep first occurrence)
    const seenSaved = new Set();
    const validSavedOrder = savedOrder.filter(
      colName => currentColumnNames.has(colName) && (seenSaved.has(colName) ? false : (seenSaved.add(colName), true))
    );

    // Build a map of default indices for quick lookup
    const defaultIndexMap = new Map();
    currentColumns.forEach((col, index) => {
      defaultIndexMap.set(col.name, index);
    });

    // Start with saved order (filtered and deduplicated)
    const finalOrder = [...validSavedOrder];
    const addedColumns = new Set(validSavedOrder);

    // Insert new columns (not in saved order) at positions based on their default indices
    currentColumns.forEach((col, defaultIndex) => {
      if (!addedColumns.has(col.name)) {
        // Count how many saved columns have a default index less than this column's default index
        // This helps us place the new column in a position that respects both saved and default orders
        let insertPosition = 0;
        for (let i = 0; i < finalOrder.length; i++) {
          const colInOrder = finalOrder[i];
          const colDefaultIndex = defaultIndexMap.get(colInOrder);
          if (colDefaultIndex !== undefined && colDefaultIndex < defaultIndex) {
            insertPosition = i + 1;
          } else if (colDefaultIndex !== undefined && colDefaultIndex > defaultIndex) {
            // Found a column that should come after this one, insert before it
            break;
          }
        }
        // Ensure we don't go beyond array length
        insertPosition = Math.min(insertPosition, finalOrder.length);
        finalOrder.splice(insertPosition, 0, col.name);
        addedColumns.add(col.name);
      }
    });

    // Final safety: ensure exactly one entry per current column (no duplicates, no missing)
    // Prevents duplicate action column or misaligned headers when columns change
    const seenFinal = new Set();
    const result = [];
    for (const name of finalOrder) {
      if (currentColumnNames.has(name) && !seenFinal.has(name)) {
        seenFinal.add(name);
        result.push(name);
      }
    }
    for (const col of currentColumns) {
      if (!seenFinal.has(col.name)) {
        result.push(col.name);
      }
    }
    return result;
  }, []);

  // Recompute validated order whenever columns or saved order change, so added/removed
  // columns (e.g. from feature flags) appear in the correct position instead of at the start
  const validatedColumnOrder = useMemo(() => {
    const savedOrder = tableSettingsStoredData?.[COLUMN_ORDER];
    if (savedOrder == null) {
      return columns.map(column => column.name);
    }
    return getValidatedColumnOrder(savedOrder, columns);
  }, [columns, tableSettingsStoredData?.[COLUMN_ORDER], getValidatedColumnOrder]);

  const [customColumnOrder, setCustomColumnOrder] = useState(validatedColumnOrder);

  // Keep displayed order in sync when columns or saved order change (e.g. flags load)
  useEffect(() => {
    if (!isEqual(customColumnOrder, validatedColumnOrder)) {
      setCustomColumnOrder(validatedColumnOrder);
    }
  }, [validatedColumnOrder]);

  // Always pass an order that exactly matches current columns to avoid misaligned or
  // duplicate headers when columns change (e.g. verification in, confidence/identifiedVia out)
  const effectiveOrder = useMemo(() => getValidatedColumnOrder(customColumnOrder, columns), [customColumnOrder, columns, getValidatedColumnOrder]);

  // Safety net: If a locked column somehow gets moved, restore it to original position
  // This is a backup since we prevent dragging at the HeaderCell level
  const handleTableColumnReordering = useCallback(
    columnOrder => {
      if (!tableStorageId || !columnOrder.length || lockedCols.length === 0) {
        // No locked columns, just update the order
        if (tableStorageId && columnOrder.length) {
          setCustomColumnOrder(columnOrder);
          setStorageKeyData(tableStorageId, { key: COLUMN_ORDER, value: columnOrder });
        }
        return;
      }

      let result = [...columnOrder];
      let needsCorrection = false;

      // Check if any locked column is out of place and restore it
      lockedCols.forEach(col => {
        const currentIndex = result.indexOf(col.name);
        if (currentIndex !== -1 && currentIndex !== col.originalIndex) {
          // Remove from wrong position
          result.splice(currentIndex, 1);
          // Reinsert at correct position
          result.splice(col.originalIndex, 0, col.name);
          needsCorrection = true;
        }
      });

      const finalOrder = needsCorrection ? result : columnOrder;

      // Only update state if order actually changed
      setCustomColumnOrder(prevOrder => {
        if (JSON.stringify(prevOrder) === JSON.stringify(finalOrder)) {
          return prevOrder;
        }
        return finalOrder;
      });

      setStorageKeyData(tableStorageId, { key: COLUMN_ORDER, value: finalOrder });
    },
    [tableStorageId, lockedCols, setStorageKeyData]
  );

  useEffect(() => {
    if (scrollToRowId != null) {
      vtRef.current.scrollToRow(scrollToRowId - 1);
    }
  }, [scrollToRowId]);

  useEffect(() => {
    if (shouldClearSelection) {
      setSelection([]);
      setShouldClearSelection(false);
    }
  }, [shouldClearSelection]);

  useEffect(() => {
    if (!!preSettedFilters) {
      setFilters(preSettedFilters);
      if (handleFiltersChange) {
        debounceHandleFiltersChange.current(preSettedFilters);
      }
    }
  }, [preSettedFilters]);

  /*GraphQL Config*/
  const [postRequestQl, result] = useNRMutation(
    qlQuery || PLACEHOLDER_MUTATION,
    getDataFrom,
    CONSTANTS.TABLE_UPDATE.SUCCESS_MESSAGE,
    CONSTANTS.TABLE_UPDATE.ERROR_MESSAGE
  );

  const handleSaveRow = (id, data, setRowEditOff) => {
    postRequestQl({
      variables: { args: { id: id, ...data } },
      context: { version: qlVersion },
      update(cache, { data }) {
        if (!!updateAfterUpdate) {
          updateAfterUpdate(cache, { id: id, data });
        }
        if (!!setRowEditOff) setRowEditOff();
      }
    });
  };

  useImperativeHandle(ref, () => ({
    refreshPagination: () => setCurrentPage(0),
    clearTableFilters: () => setFilters([]),
    tablePageSize,
    filters
  }));

  useEffect(() => {
    if (result && !isEmpty(result)) {
      const index = rows.findIndex(e => e.id === result.id);
      if (index > -1) {
        setEditingIds(editingIds.filter(e => e !== index));
      }
    }
  }, [result]);

  const [localDefaultHiddenColumnNames] = useState(
    tableSettingsStoredData?.[HIDDEN_COLUMN_NAMES] ||
      columns
        .map(e => {
          if (e.hideColumn) return e.name;
        })
        .filter(Boolean)
  );
  const infoCol = {
    title: { value: '' },
    name: CONSTANTS.TABLE.COLUMN.INFO,
    hideFilter: true,
    disableHideColumn: true
  };

  const infoColExt = {
    columnName: CONSTANTS.TABLE.COLUMN.INFO,
    align: 'center',
    width: 48
  };

  if (!!infoCellProvider) {
    if (columns.every(col => col.name !== infoCol.name)) {
      columns.unshift(infoCol);
    }
    if (columnExtensions.every(colExt => colExt.columnName !== infoColExt.columnName)) {
      columnExtensions.unshift(infoColExt);
    }
  }

  const [tableColumnVisibilityColumnExtensions] = useState(
    columns
      .map(e => {
        if (e.disableHideColumn) return { columnName: e.name, togglingEnabled: false };
      })
      .filter(Boolean)
  );

  const handlePageSizeChange = size => {
    handleFetchMore({ first: size });
    setCurrentPage(0);
    setTablePageSize(size);
  };

  const handlePageChange = p => {
    if (p > currentPage) {
      //clicked next page
      if (pageInfo.hasNextPage && pageInfo.endCursor) {
        handleFetchMore({ after: pageInfo.endCursor, first: tablePageSize });
      }
      if (pageInfo.nextPageToken) {
        // this is cursorV3
        handleFetchMore({ after: pageInfo.nextPageToken, first: tablePageSize });
      }
    } else if (p < currentPage && p !== 0) {
      //clicked prev page
      if (pageInfo.startCursor) {
        handleFetchMore({ before: pageInfo.startCursor, last: tablePageSize });
      }
      if (pageInfo.prevPageToken) {
        // this is cursorV3
        handleFetchMore({ before: pageInfo.prevPageToken, last: tablePageSize });
      }
    } else if (p === 0) {
      handleFetchMore({ first: tablePageSize });
    }
    setCurrentPage(p);
  };

  const handleOnSortingChange = value => {
    setSorting(value);
    setCurrentPage(0);

    if (sortingColumnNames && value) {
      const sortValue = { field: sortingColumnNames[value[0]?.columnName], order: value[0]?.direction.toUpperCase() };
      if (sortValue && onSortingChange) {
        onSortingChange(sortValue.field, sortValue.order);
      }
      setStorageKeyData(tableStorageId, { key: SORT, value: sortValue });
    }
  };

  const debounceHandleFiltersChange = useRef(
    debounce(filters => {
      handleFiltersChange(filters);
    }, 800)
  );

  const handleFilterChange = filters => {
    setFilters(filters);
    if (handleFiltersChange) {
      debounceHandleFiltersChange.current(filters);
    }
  };

  const filteringColumnExtensions = (value, filter, row, column) => {
    if (!filter.value) return true;
    return IntegratedFiltering.defaultPredicate(value.length || value, filter, row);
  };
  const filteringColumnExtensionsMultiselect = (value, filter, row, column) => {
    if (!filter.value) return true;
    if (!filter.value?.includes('&')) {
      return IntegratedFiltering.defaultPredicate(value, filter, row);
    } else {
      let values = filter.value.split('&');
      if (!row[filter.columnName]) return false;
      if (values.includes(row[filter.columnName].toString())) return true;
    }
    return false;
  };

  const multiselectColumnsFilteringExtensions = columns
    .map(e => {
      if (e?.type === 'multiselectFE') {
        return {
          columnName: e.name,
          predicate: filteringColumnExtensionsMultiselect
        };
      }
    })
    .filter(Boolean);

  const [integratedFilteringColumnExtensions] = useState([
    { columnName: 'correlations', align: 'center', predicate: filteringColumnExtensions },
    ...multiselectColumnsFilteringExtensions
  ]);

  const handleFiltersVisibility = () => {
    setFiltersVisibilityValue(!filtersVisibilityValue);
    if (!!onChangeFiltersVisibility) {
      onChangeFiltersVisibility(!filtersVisibilityValue);
    }
    setStorageKeyData(tableStorageId, { key: FILTER_VISIBILITY_VALUE, value: !filtersVisibilityValue });
  };

  const commitChanges = ({ changed, ...rest }) => {
    const rowIndex = Object.keys(changed)[0];
    const row = rows[rowIndex];
    handleSaveRow(row.id, changed[rowIndex], () => setEditingIds(editingIds.filter(e => e !== +rowIndex)));
  };

  const editingStateRef = useRef(null);

  const handleOnSave = id => {
    editingStateRef.current.commitChangedRows({ rowIds: [id] });
  };

  const handleDiscardChanges = id => {
    editingStateRef.current.cancelChangedRows({ rowIds: [id] });
    editingStateRef.current.stopEditRows({ rowIds: [id] });
  };

  useEffect(() => {
    setSelection([]);
  }, [rows]);

  useEffect(() => {
    if (tableStorageId && externalHiddenColumnNames) {
      setStorageKeyData(tableStorageId, { key: HIDDEN_COLUMN_NAMES, value: [...externalHiddenColumnNames] });
    }
  }, [externalHiddenColumnNames]);

  useEffect(() => {
    const oldQuery = new URLSearchParams(location.search);
    const queryObject = Object.fromEntries(oldQuery.entries());
    // Filter out 'tab' and 'subTab' and check if there are other query params
    const hasOtherParams = [...oldQuery.keys()].some(key => key !== 'tab' && key !== 'subTab' && key !== 'filePath');

    if (oldQuery.size >= 1 && hasOtherParams) {
      setShowResetFilters(true);
    } else {
      setShowResetFilters(false);
    }
  }, [location]);

  const handleResetFilters = () => {
    const oldQuery = new URLSearchParams(location.search);
    const queryObject = Object.fromEntries(oldQuery.entries());
    clearSessionKeyData();
    const query = new URLSearchParams();
    if (queryObject?.tab) {
      query.set('tab', queryObject?.tab);
    }
    if (queryObject.subTab) {
      query.set('subTab', queryObject.subTab);
    }
    setFilters([]);
    navigate(`${location.pathname}?${query.toString()}`, { replace: true });
  };

  const renderNoDataCell = useCallback(props => <NoDataCellComponent {...props} loading={loading} />, [loading]);

  // Custom cell component - width constraints now handled via CSS with data-nr-col attribute
  const CustomCellComponent = useCallback(props => {
    return <CellComponent {...props} />;
  }, []);

  // Custom header cell component - width constraints now handled via CSS with data-nr-col attribute
  const CustomHeaderCellComponent = useCallback(
    props => {
      const isLocked = lockedColNames.has(props.column.name);

      if (isLocked) {
        return <LockedHeaderCell headerCellProps={props} />;
      }

      return <HeaderCell {...props} />;
    },
    [lockedColNames]
  );

  const MemoizedCursorPagerComponent = React.memo(CursorPagerComponent);

  // Generate CSS rules for column width constraints
  // Target by data-nr-col attribute instead of nth-child to avoid index collisions (inner tables, selection column, etc.)
  const columnWidthStyles = useMemo(() => {
    if (!columnExtensions || !Array.isArray(columnExtensions) || !columns || !Array.isArray(columns)) return {};

    // Calculate column offset based on additional columns at the start
    let columnOffset = 0;

    // Add 1 for selector column if selection is enabled
    if (showIntegratedSelection) {
      columnOffset += 1;
    }

    // Add 1 for info column if infoCellProvider exists
    if (!!infoCellProvider) {
      columnOffset += 1;
    }

    // Note: Edit column is added at the END via Getter, not at the start

    const styles = {};
    columnExtensions.forEach(ext => {
      if ((ext.minWidth || ext.maxWidth || ext.width) && ext.columnName) {
        // Target by attribute to avoid alignment/index issues across tables
        const cellSelector = `& td[data-nr-col='${ext.columnName}'], & th[data-nr-col='${ext.columnName}']`;

        const cellStyles = {
          boxSizing: 'border-box'
        };

        // If width is specified, enforce it with both minWidth and maxWidth
        if (ext.width) {
          cellStyles.width = `${ext.width}px !important`;
          cellStyles.minWidth = `${ext.width}px !important`;
          cellStyles.maxWidth = `${ext.width}px !important`;
        } else {
          // Otherwise apply minWidth and maxWidth independently
          if (ext.minWidth) {
            cellStyles.minWidth = `${ext.minWidth}px !important`;
          }
          if (ext.maxWidth) {
            cellStyles.maxWidth = `${ext.maxWidth}px !important`;
          }
        }

        styles[cellSelector] = cellStyles;
      }
    });

    return styles;
  }, [columnExtensions, columns, showIntegratedSelection, infoCellProvider]);

  const overlayAnchorElRef = useRef(null);
  const [anchorElReady, setAnchorElReady] = useState(false);
  const [isTableUnstable, setIsTableUnstable] = useState(true);
  const stabilityTimerRef = useRef(null); // Ref to hold the timeout ID for the debounce

  useEffect(() => {
    setIsTableUnstable(true);
    if (stabilityTimerRef.current) {
      clearTimeout(stabilityTimerRef.current);
    }
    stabilityTimerRef.current = setTimeout(() => {
      !loading && setIsTableUnstable(false); // Mark as stable
      stabilityTimerRef.current = null; // Clear the ref
    }, 500); // Adjust this delay (e.g., 200ms, 300ms, 500ms)
    return () => {
      if (stabilityTimerRef.current) {
        clearTimeout(stabilityTimerRef.current);
      }
    };
  }, [loading, overlayAnchorElRef.current, anchorElReady]);

  const OverlayComponent = useCallback(
    props => {
      if (!overlayAnchorElRef.current || !document.body.contains(props?.target)) return null;
      if (!anchorElReady && props?.target === overlayAnchorElRef.current && document.body.contains(props?.target)) {
        setAnchorElReady(true);
      }
      return <ColumnChooser.Overlay elevation={3} {...props} target={overlayAnchorElRef.current} />;
    },
    [overlayAnchorElRef.current]
  );

  const ToggleButtonComponent = useCallback(
    ({ onToggle, buttonRef }) => (
      <Box>
        {!hideCustomColumns && (
          <Tooltip title={isTableUnstable ? '' : 'Custom columns'}>
            <Box
              component="span"
              sx={{
                position: 'absolute',
                top: !!selection.length ? -88 : -52,
                right: !!selectAllFetchConfig ? 120 : 68,
                display: 'inline-flex'
              }}
            >
              <IconButton
                onClick={onToggle}
                disabled={isTableUnstable}
                aria-label="Toggle custom columns"
                ref={node => {
                  if (typeof buttonRef === 'function') {
                    buttonRef(node);
                  }
                  if (node && node instanceof HTMLElement) {
                    if (document.body.contains(node) && node !== overlayAnchorElRef.current) {
                      overlayAnchorElRef.current = node;
                    }
                  }
                }}
                color="secondary"
                size="small"
                sx={{
                  padding: 0.75,
                  color: theme.palette.text.icon
                }}
              >
                <NRColumnSettingsIcons />
              </IconButton>
            </Box>
          </Tooltip>
        )}
        {!hideColumnFiltering && (
          <Tooltip title="Column filtering">
            <ToggleButton
              value="filtersVisibility"
              selected={filtersVisibilityValue}
              onChange={handleFiltersVisibility}
              aria-label={`${filtersVisibilityValue ? 'Hide' : 'Show'} column filtering`}
              aria-pressed={filtersVisibilityValue}
              sx={{
                padding: 0.75,
                border: 'unset',
                position: 'absolute',
                top: !!selection.length ? -88 : -52,
                right: !!selectAllFetchConfig ? 68 : 16,
                color: !!filters?.length ? `${theme.palette.secondary.contrastText} !important` : theme.palette.text.icon,
                ...(!!filters?.length && {
                  backgroundColor: `${theme.palette.secondary.light} !important`
                })
              }}
            >
              <NRFilterListIcon />
            </ToggleButton>
          </Tooltip>
        )}

        {!!selectAllFetchConfig && (
          <Tooltip title="Export to CSV">
            <IconButton
              onClick={() => setExportToCsvDialogIsOpen(true)}
              aria-label="Export to CSV"
              color="secondary"
              size="small"
              sx={{
                padding: 0.75,
                position: 'absolute',
                top: !!selection.length ? -88 : -52,
                right: 16
              }}
              disabled={!selectAllFetchConfig}
            >
              <NRExportIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    ),
    [
      filtersVisibilityValue,
      handleFiltersVisibility,
      selection.length,
      selectAllFetchConfig,
      setExportToCsvDialogIsOpen,
      filters?.length,
      theme.palette
    ]
  );

  return (
    <Paper
      variant={variant}
      {...(variant === 'elevation' && { elevation })}
      sx={{
        '.TableFilterRow-row > th.TableStubCell-cell': {
          borderBottom: `1px solid ${theme.palette.divider}`
        },
        '& .MuiTableCell-root .TableSelectCell-checkbox': {
          // Forces minimum height of 48px for the row
          marginTop: '0px',
          marginBottom: '0px',
          padding: '12px'
        },
        overflow: 'auto',
        ...(fullHeight && {
          height: '100%',
          '& .Layout-root': {
            height: 'calc(100% - 69px)'
          }
        }),
        ...columnWidthStyles
      }}
      {...(!!customClass && { className: customClass })}
    >
      <NRExportToCsvDialog
        isOpen={exportToCsvDialogIsOpen}
        setIsOpen={setExportToCsvDialogIsOpen}
        selectAllFetchConfig={selectAllFetchConfig}
        totalCount={totalCount}
        specialCase={specialCaseCSV}
      />
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {!hideTableHeaderRow && showTitle && (
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', height: 68 }}>
            {!!tableIcon && <StyledAvatar variant="rounded" children={tableIcon} />}
            <Box display={'flex'} alignItems={'center'}>
              <StyledGridTitle id={titleId} children={title} />
              <Box>{titleRightIcon}</Box>
            </Box>

            <Box
              p={0}
              ml={'auto'}
              mr={
                !!titleExtraComponent
                  ? 0
                  : !!selectAllFetchConfig
                  ? (!!hideColumnFiltering ? 0 : 6.5) + (!!hideCustomColumns ? 0 : 6.5)
                  : (!!hideColumnFiltering ? 0 : 3) + (!!hideCustomColumns ? 0 : 3) + 0.5
              }
              display={'flex'}
              alignItems={'center'}
            >
              {' '}
              {showResetFilters && (
                <Button
                  variant="text"
                  onClick={handleResetFilters}
                  sx={{
                    color: theme.palette.blue.link,
                    fontWeight: 400,
                    lineHeight: 1.42,
                    letterSpacing: '0.01em',
                    textTransform: 'none',
                    mr: !!titleExtraComponent?.length ? 2 : 6.5,
                    minWidth: 'auto',
                    padding: 0
                  }}
                >
                  Reset filters
                </Button>
              )}
              {!!titleExtraComponent && titleExtraComponent}
            </Box>
          </Box>
        )}
        {!!selection.length && (
          <CopyToClipboardBanner
            selectedItems={selection}
            setSelection={setSelection}
            rows={rows}
            totalCount={totalCount}
            selectAllFetchConfig={selectAllFetchConfig}
            showSelectAllItems={selection.length === tablePageSize}
          />
        )}
        {fullHeight && <Divider sx={{ width: '100%', alignSelf: 'center' }} />}
      </Box>
      <Box role="region" aria-labelledby={titleId}>
        <Grid
          rows={rows}
          columns={columns}
          key={`${tableKey || 'table'}-${columns.length}-${columns
            .map(c => c.name)
            .sort()
            .join(',')}`}
        >
          <SelectionState selection={selection} onSelectionChange={setSelection} />
          <FilteringState filters={filters} onFiltersChange={handleFilterChange} />
          <SortingState
            sorting={sorting}
            onSortingChange={handleOnSortingChange}
            {...(!!addDefaultSorting && {
              defaultSorting: [
                { columnName: defaultSortingColumn || columns[!!infoCellProvider ? 1 : 0]?.name, direction: defaultSortingOrder?.toLowerCase() }
              ]
            })}
          />
          {showIntegratedPagination && !hasCursorPagination && (
            <PagingState
              defaultCurrentPage={0}
              defaultPageSize={tablePageSize}
              {...(!!handleOnPageSizeChange && { onPageSizeChange: handleOnPageSizeChange })}
            />
          )}
          {showIntegratedPagination && hasCursorPagination && (
            <PagingState
              defaultCurrentPage={0}
              defaultPageSize={tablePageSize}
              onCurrentPageChange={handlePageChange}
              currentPage={currentPage}
              onPageSizeChange={handlePageSizeChange}
            />
          )}
          {(isTableEditable || hasExtraActions) && (
            <EditingState onCommitChanges={commitChanges} onEditingRowIdsChange={setEditingIds} editingRowIds={editingIds} ref={editingStateRef} />
          )}
          {/* place it after IntegratedPaging for the select all to affect all pages */}
          {statesArray}
          <IntegratedSelection />
          {!hasCursorPagination && <IntegratedFiltering columnExtensions={integratedFilteringColumnExtensions} />}
          {!hasCursorPagination && <IntegratedSorting columnExtensions={sortingColumnExtension} />}
          {[...providersArray, infoCellProvider]}
          {showIntegratedPagination && !hasCursorPagination && <IntegratedPaging />}
          {showIntegratedPagination && hasCursorPagination && <CustomPaging totalCount={totalCount} />}
          {!isVirtualTable && (
            <Table
              columnExtensions={columnExtensions}
              tableComponent={TableComponent}
              cellComponent={CustomCellComponent}
              noDataCellComponent={renderNoDataCell}
              rowComponent={RowComponent}
            />
          )}
          {isVirtualTable && (
            <VirtualTable
              columnExtensions={columnExtensions}
              tableComponent={TableComponent}
              cellComponent={CustomCellComponent}
              noDataCellComponent={renderNoDataCell}
              rowComponent={RowComponent}
              ref={vtRef}
            />
          )}
          {allowColumnReOrdering && (
            <DragDropProvider
              columnComponent={({ column }) => {
                return (
                  // to support value based column title
                  <DragDropProvider.Column
                    {...{
                      column: {
                        disableEdit: column.disableEdit,
                        name: column.name,
                        title: column.title.value
                      }
                    }}
                  />
                );
              }}
            />
          )}
          {allowColumnReOrdering && (
            <TableColumnReordering order={effectiveOrder} defaultOrder={validatedColumnOrder} onOrderChange={handleTableColumnReordering} />
          )}
          <TableHeaderRow showSortingControls sortLabelComponent={SortLabel} cellComponent={CustomHeaderCellComponent} />
          {filtersVisibilityValue && (
            <TableFilterRow
              rowComponent={FilterRow}
              cellComponent={FilterCell}
              toggleButtonComponent={ToggleComponent}
              {...(showFilterSelector && { showFilterSelector })}
            />
          )}
          <TableColumnVisibility
            defaultHiddenColumnNames={localDefaultHiddenColumnNames}
            columnExtensions={tableColumnVisibilityColumnExtensions}
            onHiddenColumnNamesChange={hiddenColumnNames => {
              if (tableStorageId) {
                setStorageKeyData(tableStorageId, { key: HIDDEN_COLUMN_NAMES, value: [...hiddenColumnNames] });
              }
            }}
            {...(externalHiddenColumnNames && {
              hiddenColumnNames: externalHiddenColumnNames
            })}
          />
          <Toolbar rootComponent={props => <Toolbar.Root {...props} style={{ minHeight: 0, borderBottom: 'unset' }} />} />
          <ColumnChooser
            toggleButtonComponent={ToggleButtonComponent}
            itemComponent={({ onToggle, disabled, item }) => {
              if (nonDisableAbleColumns.includes(item.column.name)) return null;
              return (
                <ColumnChooser.Item
                  {...{
                    disabled,
                    onToggle,
                    item: {
                      hidden: item.hidden,
                      column: {
                        name: item.column.name,
                        title: item.column.title.value,
                        getCellValue: item.column.getCellValue
                      }
                    }
                  }}
                />
              );
            }}
            overlayComponent={OverlayComponent}
          />
          {(isTableEditable || hasExtraActions) && (
            <TableEditRow
              cellComponent={props => (
                <EditRowCustomCellComponent
                  {...props}
                  customStyle={{
                    '&:focus-within': {
                      border: `1px solid ${theme.palette.primary.main} !important`
                    }
                  }}
                  onKeyDown={e => {
                    if (e.keyCode === 13) {
                      // Enter
                      handleOnSave(props?.tableRow?.rowId);
                    }
                    if (e.keyCode === 27) {
                      // Escape
                      handleDiscardChanges(props?.tableRow?.rowId);
                    }
                  }}
                />
              )}
              rowComponent={StyledTableEditRow}
            />
          )}
          {(isTableEditable || hasExtraActions) && (
            <TableEditColumn
              showEditCommand={!!isTableEditable}
              cellComponent={EditColumnCustomCellComponent}
              headerCellComponent={HeaderCellComponent}
              commandComponent={Command}
              showAddCommand
              width={82}
            />
          )}
          {renderersArray}
          {showIntegratedSelection && (
            <TableSelection
              showSelectAll={showSelectAll}
              {...(!!selectionCell && !!showIntegratedSelection && { cellComponent: selectionCell })}
              {...((!!selectAllHeaderCell || !!selectAllAriaLabel) && {
                headerCellComponent: props => {
                  const HeaderCell = selectAllHeaderCell || SelectAllHeaderCell;
                  return <HeaderCell {...props} selectAllAriaLabel={selectAllAriaLabel} />;
                }
              })}
            />
          )}
          <Getter
            name="tableColumns"
            computed={({ tableColumns }) => {
              if (isEqual(tableColumns, lastInput)) {
                return lastResult;
              }
              lastInput = tableColumns;
              lastResult = [
                ...tableColumns.filter(c => c.type.toString() !== 'Symbol(editCommand)'),
                ...tableColumns.filter(c => c.type.toString() === 'Symbol(editCommand)')
              ];
              return lastResult;
            }}
          />
          <Action name="cancelChangedRows" action={({ rowIds }) => editingIdSetter(rowIds, editingIds, setEditingIds)} />
          {showIntegratedPagination && (
            <PagingPanel pageSizes={pageSizes} {...(!!hasCursorPagination && { containerComponent: MemoizedCursorPagerComponent })} />
          )}
        </Grid>
      </Box>
    </Paper>
  );
}
export default forwardRef(NRReactGrid);
